import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

/**
 * @param {boolean} isDirty - whether the form has unsaved changes
 * @param {object} [options]
 * @param {boolean} [options.blockNavigation=true] - block route navigation via useBlocker
 * @param {boolean} [options.blockUnload=true] - warn on browser tab close/refresh
 */
export const useUnsavedChanges = (isDirty, options = {}) => {
  const { blockNavigation = true, blockUnload = true } = options;
  const [showDialog, setShowDialog] = useState(false);
  const pendingActionRef = useRef(null);
  const bypassRef = useRef(false);

  const blocker = useBlocker(
    () => blockNavigation && isDirty && !bypassRef.current,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowDialog(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    if (!blockUnload || !isDirty) return;
    const handler = (e) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, blockUnload]);

  const confirmDiscard = useCallback(() => {
    setShowDialog(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  }, [blocker]);

  const cancelDiscard = useCallback(() => {
    setShowDialog(false);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    pendingActionRef.current = null;
  }, [blocker]);

  const guardAction = useCallback(
    (action) => {
      if (!isDirty) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setShowDialog(true);
    },
    [isDirty],
  );

  const bypass = useCallback(() => {
    bypassRef.current = true;
  }, []);

  return {
    showDialog,
    confirmDiscard,
    cancelDiscard,
    guardAction,
    bypass,
  };
};
